import { type JSX } from "react";
import type { components } from "../../../api/schema";
import { SceneNode } from "./SceneNode";
import { classifyTrace, type TraceStatus } from "./traceClassifier";

type Scene = components["schemas"]["Scene"];
type WorldlineNode = components["schemas"]["WorldlineNode"];

export type TraceRowProps = {
  worldline: WorldlineNode;
  /** 該当 worldline の scenes (古い順、time-asc)。 */
  scenes: Scene[];
  /**
   * 時系列上の筆跡順序 (0 = 第一筆跡, traceCount - 1 = 最新)。
   * CSS opacity の線形補間に使う (0.3 → 1.0)。
   */
  traceIndex: number;
  /** 全筆跡総数。opacity 補間の分母。 */
  traceCount: number;
  /** プレイヤーが現在プレイ中の worldline id。classifyTrace で ongoing 判定に使う。 */
  currentWorldlineId: string | null;
  /** 骨格 beat に該当する scene id 集合 (SceneNode.isBeat 判定用)。 */
  beatSceneIds?: Set<string>;
  /** scene ノード click / tap 時のコールバック。 */
  onTapScene: (scene: Scene, worldline: WorldlineNode) => void;
};

const MIN_OPACITY = 0.3;
const MAX_OPACITY = 1.0;

/**
 * 時系列筆跡順序に応じた CSS opacity を線形補間で計算する。
 *  - traceCount=1: 単一筆跡は 1.0 (最新扱い)
 *  - 複数筆跡: 第一筆跡 0.3、最新 1.0、間は線形補間
 */
export function computeTraceOpacity(
  traceIndex: number,
  traceCount: number,
): number {
  if (traceCount <= 1) return MAX_OPACITY;
  const clamped = Math.max(0, Math.min(traceIndex, traceCount - 1));
  const ratio = clamped / (traceCount - 1);
  return MIN_OPACITY + ratio * (MAX_OPACITY - MIN_OPACITY);
}

const STATUS_CLASS: Record<TraceStatus, string> = {
  completed: "palimpsest-trace--completed border-amber-500/40",
  ongoing: "palimpsest-trace--ongoing border-indigo-400/70",
  interrupted: "palimpsest-trace--interrupted border-slate-700/50",
};

/**
 * worldline-palimpsest (Phase 3-1, Task 5.5) 1 worldline = 1 行の筆跡描画。
 *
 * 横方向に scenes を SceneNode で並べ、時系列順 (traceIndex) に応じた
 * CSS opacity で「古い筆跡は薄く、最新はくっきり」を表現する (palimpsest メタファ)。
 * 3 状態 (completed / ongoing / interrupted) は CSS class で区別する (Req 6.4 色 + 分類)。
 */
export function TraceRow({
  worldline,
  scenes,
  traceIndex,
  traceCount,
  currentWorldlineId,
  beatSceneIds,
  onTapScene,
}: TraceRowProps): JSX.Element {
  const status = classifyTrace(worldline, scenes.length, currentWorldlineId);
  const opacity = computeTraceOpacity(traceIndex, traceCount);

  return (
    <div
      data-testid={`palimpsest-trace-row-${worldline.id}`}
      data-trace-status={status}
      data-trace-index={traceIndex}
      className={[
        "relative flex items-center gap-2 px-2 py-1 border-l-2 rounded-sm",
        STATUS_CLASS[status],
      ].join(" ")}
      style={{ opacity }}
      aria-label={`筆跡 ${worldline.id.slice(0, 8)} (${status})`}
    >
      {scenes.length === 0 ? (
        <span className="text-[10px] text-slate-500">(シーン未生成)</span>
      ) : (
        scenes.map((scene, i) => (
          <SceneNode
            key={scene.id}
            scene={scene}
            isBeat={beatSceneIds?.has(scene.id) === true}
            label={`${i + 1}`}
            onTap={(s) => onTapScene(s, worldline)}
          />
        ))
      )}
    </div>
  );
}
